import { getIsMasterTablet } from "../browser/localStorage";
import { log } from "../logger";

// ──────────────────────────────────────────────────────────────────────────────────────────────
// TEMP diagnostic. Mirrors the [state-debug] console lines into an in-memory ring buffer so the
// on-screen LightingDebugOverlay can show — and let you copy — the logs directly on a kiosk tablet
// (Hexnode Browser Lite), where remote Web Inspector is unavailable.
//
// Enabled only on the MASTER tablet AND when the `lightingDebug` flag is set (URL `?lightingDebug=true`
// is persisted to localStorage so it survives kiosk reloads; `?lightingDebug=false` clears it). This
// keeps it off for customers and out of integration tests / standard tablets.
//
// Remove this file together with stateDebugMiddleware and LightingDebugOverlay once the cause is found.
// ──────────────────────────────────────────────────────────────────────────────────────────────

const MAX_LINES = 500;
let lines: string[] = [];
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) {
		listener();
	}
};

const pad = (value: number) => String(value).padStart(2, "0");
const timestamp = () => {
	const now = new Date();
	return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

/** Log to the console AND append to the on-screen overlay buffer. */
export const debugTrace = (message: string) => {
	log(message, "info");
	const line = `${timestamp()} ${message}`;
	lines = lines.length >= MAX_LINES ? [...lines.slice(1), line] : [...lines, line];
	notify();
};

export const subscribeDebugTrace = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

// Stable reference between writes → safe for useSyncExternalStore.
export const getDebugTraceSnapshot = () => lines;

export const clearDebugTrace = () => {
	lines = [];
	notify();
};

export const isLightingDebugFlagEnabled = (): boolean => {
	try {
		const param = new URLSearchParams(window.location.search).get("lightingDebug");
		if (param === "true") {
			localStorage.setItem("lightingDebug", "true");
		} else if (param === "false") {
			localStorage.removeItem("lightingDebug");
		}
		return localStorage.getItem("lightingDebug") === "true";
	} catch {
		return false;
	}
};

/** True only on the master tablet with the diagnostic flag enabled. */
export const shouldTraceLightingDebug = (): boolean => getIsMasterTablet() && isLightingDebugFlagEnabled();
